// Per-chapter configuration. Keyed by the exact chapter name stored on the
// LP user record (user.chapter) and on every pitch doc (pitch.chapter).
//
// THIS IS THE SINGLE SOURCE OF TRUTH FOR CHAPTER MAP + GEO DATA. To add a
// chapter, add one entry below with:
//   - center:      { lat, lng, zoom } — where the maps center for this chapter
//                  AND the fallback point for any pitch whose zip has no precise
//                  coordinates yet. Drives FounderMap + PitchMap automatically.
//   - zipPrefixes: 3-digit zip prefixes routed to this chapter. Drives both
//                  zip->chapter detection (concierge wizard) and the map's
//                  chapter-center geocoding fallback. A prefix must belong to
//                  exactly ONE chapter — a shared prefix is silently won by
//                  whichever chapter is defined later in this object.
//   - zipOverrides: exact 5-digit zips routed to this chapter even though
//                  their 3-digit prefix belongs to another chapter. Checked
//                  BEFORE the prefix lookup. Use when a chapter's counties
//                  straddle a prefix another chapter owns (e.g. Hudson Valley's
//                  Ulster towns inside Capital Region's 124, or Capital
//                  Region's southern-Columbia towns inside Hudson Valley's 125).
//   - Stripe links (lpMembership / gnfChapterFee / donation). Leave a URL empty
//                  to hide that button for the chapter. Mint the standard set
//                  with functions/scripts/provisionChapterStripe.js.
//
// After adding a chapter here, also mirror its zipPrefixes AND zipOverrides
// into the backend copy at functions/zipToChapter.js (Cloud Functions are
// CommonJS and can't import this file), then redeploy functions. For
// pin-accurate placement add the chapter's real zip->lat/lng rows to
// public/data/zipcodes.json; until then the map falls back to `center`.

const CHAPTER_CONFIG = {
  'Western New York': {
    center: { lat: 42.89, lng: -78.78, zoom: 8 }, // Buffalo metro (chapter hub + fallback pin point)
    zipPrefixes: [140, 141, 142, 143, 144, 145, 146, 147, 148],
    lpMembership: {
      annualUrl: 'https://buy.stripe.com/fZu8wPb2Edeo4jE68q8EM07',
      semiAnnualUrl: 'https://buy.stripe.com/14AbJ1eeQcak3fA2We8EM06',
    },
    gnfChapterFee: {
      monthlyUrl: 'https://buy.stripe.com/cNi9AT1s44HS3fA0O68EM0e',
    },
    donation: {
      oneTimeUrl: 'https://buy.stripe.com/00wcN5fiUfmw6rM2We8EM0u',
    },
  },
  'Central New York': {
    center: { lat: 43.05, lng: -76.15, zoom: 8 },
    zipPrefixes: [130, 131, 132, 133, 134, 135, 136, 137, 138, 139],
    lpMembership: {
      annualUrl: 'https://buy.stripe.com/9B66oH0o00rC4jEfJ08EM0b',
      semiAnnualUrl: 'https://buy.stripe.com/4gMcN5eeQfmw9DYdAS8EM0a',
    },
    gnfChapterFee: {
      monthlyUrl: 'https://buy.stripe.com/aFa14n0o0eisdUe40i8EM0f',
    },
    donation: {
      oneTimeUrl: 'https://buy.stripe.com/14AbJ1c6I1vGg2mfJ08EM0t',
    },
  },
  'Capital Region': {
    center: { lat: 42.65, lng: -73.75, zoom: 9 },
    // 125/126 (Dutchess, Poughkeepsie/Newburgh) and 127 (Sullivan) moved to
    // Hudson Valley. Columbia + Greene stay here, matching the NYS Capital
    // Region definition: Greene and northern Columbia sit inside our own
    // 120/121/124 prefixes, and southern Columbia's 125xx zips are claimed
    // back from Hudson Valley's 125 via zipOverrides below. Hudson Valley
    // claims its Ulster zips inside our 124 the same way.
    zipPrefixes: [120, 121, 122, 123, 124, 128, 129],
    zipOverrides: [
      // Columbia County towns inside Hudson Valley's 125 prefix (Hudson,
      // Claverack, Copake, Germantown, Hillsdale, ...)
      '12502', '12503', '12513', '12516', '12517', '12521', '12523', '12526',
      '12529', '12530', '12534', '12541', '12544', '12565',
    ],
    lpMembership: {
      annualUrl: 'https://buy.stripe.com/14A00j7Qs2zK6rMgN48EM0d',
      semiAnnualUrl: 'https://buy.stripe.com/eVqbJ1fiUcak8zU1Sa8EM0c',
    },
    gnfChapterFee: {
      monthlyUrl: 'https://buy.stripe.com/aFafZhb2E1vG5nI2We8EM0g',
    },
    donation: {
      oneTimeUrl: 'https://buy.stripe.com/8x214n9YA8Y88zU68q8EM0s',
    },
  },
  'Hudson Valley': {
    center: { lat: 41.62, lng: -74.0, zoom: 8 }, // Newburgh/mid-Hudson (chapter hub + fallback pin point)
    // Westchester 105-108, Rockland/northern Orange 109, Dutchess/Putnam 125,
    // Poughkeepsie 126, Sullivan/western Orange 127. Ulster (the seventh
    // county) sits inside prefix 124 owned by Capital Region, so its zips are
    // routed exactly, below. Columbia + Greene belong to Capital Region —
    // including southern Columbia's 125xx zips inside our 125 prefix, claimed
    // back via Capital Region's zipOverrides above.
    zipPrefixes: [105, 106, 107, 108, 109, 125, 126, 127],
    zipOverrides: [
      // Ulster County (prefix 124)
      '12401', '12402', '12404', '12409', '12410', '12411', '12412', '12416', '12417', '12419',
      '12420', '12428', '12429', '12432', '12433', '12435', '12440', '12441', '12443', '12446',
      '12448', '12449', '12453', '12456', '12457', '12458', '12461', '12464', '12465', '12466',
      '12471', '12472', '12475', '12477', '12480', '12481', '12483', '12484', '12486', '12487',
      '12489', '12490', '12491', '12493', '12494', '12495', '12498',
    ],
    lpMembership: {
      annualUrl: 'https://buy.stripe.com/bJe3cvdaMfmw6rMbsK8EM0m',
      semiAnnualUrl: 'https://buy.stripe.com/7sYbJ16Mo4HScQa1Sa8EM0n',
    },
    gnfChapterFee: {
      monthlyUrl: 'https://buy.stripe.com/fZu3cvfiU6Q04jEbsK8EM0o',
    },
    donation: {
      oneTimeUrl: 'https://buy.stripe.com/aFadR99YA4HSg2m2We8EM0q',
    },
  },
  'Denver': {
    center: { lat: 39.7392, lng: -104.9903, zoom: 10 },
    zipPrefixes: [800, 801, 802, 803, 804, 805],
    lpMembership: {
      annualUrl: 'https://buy.stripe.com/00wdR97Qs8Y86rM8gy8EM09',
      semiAnnualUrl: 'https://buy.stripe.com/00w3cv7Qs8Y803obsK8EM08',
    },
    gnfChapterFee: {
      monthlyUrl: 'https://buy.stripe.com/14A7sL8Uwcak5nI0O68EM0h',
    },
    donation: {
      oneTimeUrl: 'https://buy.stripe.com/3cIcN5fiU6Q07vQeEW8EM0r',
    },
  },
};

// Derived from CHAPTER_CONFIG so adding a chapter above updates every consumer.
export const CHAPTER_NAMES = Object.keys(CHAPTER_CONFIG);

// chapter name -> { lat, lng, zoom }. Used to center the maps and as the
// geocoding fallback point for pitches without precise zip coordinates.
export const CHAPTER_CENTERS = Object.fromEntries(
  Object.entries(CHAPTER_CONFIG)
    .filter(([, cfg]) => cfg.center)
    .map(([name, cfg]) => [name, cfg.center])
);

// 3-digit zip prefix -> chapter name. Powers zip->chapter detection and the
// chapter-center geocoding fallback. Mirror prefixes into functions/zipToChapter.js.
export const ZIP_PREFIX_TO_CHAPTER = Object.entries(CHAPTER_CONFIG).reduce(
  (map, [name, cfg]) => {
    (cfg.zipPrefixes || []).forEach((prefix) => {
      map[prefix] = name;
    });
    return map;
  },
  {}
);

// Exact 5-digit zip -> chapter name, from each chapter's zipOverrides. Checked
// before the prefix lookup by the shared zip resolver. Mirror into
// functions/zipToChapter.js alongside the prefixes.
export const EXACT_ZIP_TO_CHAPTER = Object.entries(CHAPTER_CONFIG).reduce(
  (map, [name, cfg]) => {
    (cfg.zipOverrides || []).forEach((zip) => {
      map[zip] = name;
    });
    return map;
  },
  {}
);

export function getChapterConfig(chapter) {
  return (chapter && CHAPTER_CONFIG[chapter]) || null;
}

export function getChapterCenter(chapter) {
  return getChapterConfig(chapter)?.center || null;
}

export function getChapterMembershipLinks(chapter) {
  const cfg = getChapterConfig(chapter);
  return cfg?.lpMembership || null;
}

export function getChapterFeeLinks(chapter) {
  const cfg = getChapterConfig(chapter);
  return cfg?.gnfChapterFee || null;
}

export function getDonationLinks(chapter) {
  const cfg = getChapterConfig(chapter);
  return cfg?.donation || null;
}

export default CHAPTER_CONFIG;
